/**
 * ErrorBoundary — M2.7 audit gap.
 *
 * Wraps any React subtree and catches render/lifecycle errors before they
 * white-screen the whole app. In dev mode the original error message is
 * surfaced inline; in production only the generic card is shown.
 */
import React from 'react';
import { StatusPage } from './page/StatusPage.js';

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  /** Custom fallback; defaults to the built-in error card. */
  fallback?: React.ReactNode;
  /** Called when the boundary resets its own state (not used by the default Reload button). */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught render error:', error, info);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    if (fallback !== undefined) {
      return fallback;
    }

    // Template example: NextAdmin `(pages)/error-page/page.tsx` (demo `/error-page`). The boundary can sit above
    // the router, so the action is a button, not a link.
    return (
      <div className="p-6">
        <StatusPage
          title="Something went wrong"
          description={
            <>
              <p>An unexpected error occurred. You can try reloading the page.</p>
              {import.meta.env.DEV && error && (
                <pre className="mx-auto mt-4 max-w-xl overflow-auto rounded-sm bg-danger-muted p-3 text-left text-xs text-danger">
                  {error.message}
                </pre>
              )}
            </>
          }
          action={{ label: 'Reload', onPress: () => window.location.reload(), leadingArrow: false }}
        />
      </div>
    );
  }
}
